import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  type TooltipModel,
} from 'chart.js';
import { useMemo, type ReactNode } from 'react';
import { Line } from 'react-chartjs-2';
import Select, { type StylesConfig } from 'react-select';

import { SegmentButton } from '@/components/ui/segment-button';
import {
  useCostAgents,
  useAgentTurnCurves,
  type TierFilter,
} from '@/hooks/useCostAnalytics';
import { useTheme } from '@/hooks/useTheme';
import { costAnalyticsSearchSchema } from '@/lib/route-search';
import type { ThemeMode } from '@/lib/theme';
import type {
  AgentTier,
  DaysWindow,
  SampleMode,
  TurnCurvePoint,
  TurnCurveSummary,
} from '@/types/cost-analytics';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

// ──────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────

const AGENT_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
];

const TIER_BASELINE_COLORS: Record<AgentTier, string> = {
  max: '#a855f7',
  regular: '#6b7280',
  quick: '#22d3ee',
};

const TIER_LABELS: Record<AgentTier, string> = {
  max: 'Max',
  regular: 'Regular',
  quick: 'Quick',
};

interface ThemeTokens {
  grid: string;
  tick: string;
  label: string;
  axisTitle: string;
  tooltipBackground: string;
  tooltipTitle: string;
  tooltipBody: string;
  tooltipBorder: string;
}

const TOKENS: Record<ThemeMode, ThemeTokens> = {
  dark: {
    grid: 'rgba(255, 255, 255, 0.07)',
    tick: '#9ca3af',
    label: '#e5e7eb',
    axisTitle: '#9ca3af',
    tooltipBackground: 'rgba(15, 23, 42, 0.92)',
    tooltipTitle: '#f1f5f9',
    tooltipBody: '#cbd5e1',
    tooltipBorder: 'rgba(255, 255, 255, 0.1)',
  },
  light: {
    grid: 'rgba(0, 0, 0, 0.07)',
    tick: '#6b7280',
    label: '#374151',
    axisTitle: '#6b7280',
    tooltipBackground: 'rgba(255, 255, 255, 0.97)',
    tooltipTitle: '#111827',
    tooltipBody: '#374151',
    tooltipBorder: 'rgba(0, 0, 0, 0.1)',
  },
};

type ExternalTooltipContext = {
  chart: ChartJS;
  tooltip: TooltipModel<'line'>;
};

function buildExternalTooltipHandler(tokens: ThemeTokens) {
  return (ctx: ExternalTooltipContext) => {
    const { chart, tooltip } = ctx;
    const parent = chart.canvas.parentElement;
    if (!parent) return;

    const elId = `chartjs-tooltip-${String(chart.id)}`;
    let el = parent.querySelector<HTMLDivElement>(`#${elId}`);
    if (!el) {
      el = document.createElement('div');
      el.id = elId;
      Object.assign(el.style, {
        position: 'absolute',
        pointerEvents: 'none',
        transition: 'opacity 120ms ease-out',
        zIndex: '50',
        borderRadius: '6px',
        padding: '8px 10px',
        fontSize: '12px',
        lineHeight: '1.6',
        whiteSpace: 'nowrap',
      });
      parent.appendChild(el);
    }

    if (tooltip.opacity === 0) {
      el.style.opacity = '0';
      return;
    }

    Object.assign(el.style, {
      background: tokens.tooltipBackground,
      color: tokens.tooltipBody,
      border: `1px solid ${tokens.tooltipBorder}`,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    });

    let html = '';
    if (tooltip.title?.length) {
      html += `<div style="font-weight:600;color:${tokens.tooltipTitle};margin-bottom:5px">${tooltip.title[0]}</div>`;
    }
    for (const item of tooltip.dataPoints ?? []) {
      const raw = item.raw as {
        avg_input_tokens?: number;
        avg_output_tokens?: number;
        avg_cached_read_tokens?: number;
        avg_cache_write_tokens?: number;
        avg_thought_tokens?: number;
      };
      const ds = item.dataset as {
        borderColor?: string;
        borderDash?: number[];
        label?: string;
      };
      const color = ds.borderColor ?? '#888';
      const isDashed = (ds.borderDash?.length ?? 0) > 0;
      const dot = isDashed
        ? `display:inline-block;width:10px;height:10px;border-radius:50%;flex-shrink:0;border:2px dashed ${color};background:transparent`
        : `display:inline-block;width:10px;height:10px;border-radius:50%;flex-shrink:0;background:${color}`;
      html += `<div style="display:flex;align-items:center;gap:6px;margin-bottom:2px">`;
      html += `<span style="${dot}"></span>`;
      html += `<span>${ds.label ?? ''}: $${(item.parsed.y ?? 0).toFixed(6)}</span>`;
      html += `</div>`;
      if (raw?.avg_input_tokens !== undefined) {
        html += `<div style="margin-left:16px;display:flex;gap:8px;font-size:11px;margin-bottom:3px">`;
        html += `<span style="color:#3b82f6">I&nbsp;${formatTokens(raw.avg_input_tokens)}</span>`;
        html += `<span style="color:#10b981">O&nbsp;${formatTokens(raw.avg_output_tokens ?? 0)}</span>`;
        html += `<span style="color:#f59e0b">W&nbsp;${formatTokens(raw.avg_cache_write_tokens ?? 0)}</span>`;
        html += `<span style="color:#8b5cf6">R&nbsp;${formatTokens(raw.avg_cached_read_tokens ?? 0)}</span>`;
        html += `<span style="color:#6b7280">T&nbsp;${formatTokens(raw.avg_thought_tokens ?? 0)}</span>`;
        html += `</div>`;
      }
    }
    el.innerHTML = html;

    el.style.opacity = '1';
    el.style.left = `${tooltip.caretX + 12}px`;
    el.style.top = `${Math.max(0, tooltip.caretY - 10)}px`;
  };
}

function buildChartOptions(tokens: ThemeTokens) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    // Disable entrance animation but leave tooltip animation intact
    animation: { duration: 0 },
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          pointStyleWidth: 10,
          padding: 16,
          font: { size: 11 },
          color: tokens.label,
        },
      },
      tooltip: {
        enabled: false,
        external: buildExternalTooltipHandler(tokens),
      },
    },
    scales: {
      x: {
        type: 'linear' as const,
        title: {
          display: true,
          text: 'Turn',
          font: { size: 11 },
          color: tokens.axisTitle,
        },
        ticks: { precision: 0, font: { size: 10 }, color: tokens.tick },
        grid: { color: tokens.grid },
        border: { color: tokens.grid },
      },
      y: {
        ticks: {
          font: { size: 10 },
          color: tokens.tick,
          callback: (value: number | string) => `$${Number(value).toFixed(4)}`,
        },
        grid: { color: tokens.grid },
        border: { color: tokens.grid },
      },
    },
  };
}

type SelectOption = { value: string; label: string };

function buildSelectStyles(theme: ThemeMode): StylesConfig<SelectOption, true> {
  const isDark = theme === 'dark';
  return {
    container: (base) => ({
      ...base,
      width: '75%',
    }),
    control: (base) => ({
      ...base,
      minHeight: '32px',
      width: '100%',
      fontSize: '14px',
      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#ffffff',
      borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)',
      boxShadow: 'none',
      '&:hover': {
        borderColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.3)',
      },
    }),
    menu: (base) => ({
      ...base,
      width: '100%',
      fontSize: '13px',
      zIndex: 50,
      backgroundColor: isDark ? '#1e2535' : '#ffffff',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      boxShadow: isDark
        ? '0 8px 24px rgba(0,0,0,0.5)'
        : '0 8px 24px rgba(0,0,0,0.12)',
    }),
    menuList: (base) => ({
      ...base,
      maxWidth: '100%',
    }),
    option: (base, state) => ({
      ...base,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      backgroundColor: state.isFocused
        ? isDark
          ? 'rgba(255,255,255,0.08)'
          : 'rgba(0,0,0,0.05)'
        : 'transparent',
      color: isDark ? '#e5e7eb' : '#111827',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 8px',
      flexWrap: 'wrap',
      gap: '2px',
    }),
    input: (base) => ({ ...base, color: isDark ? '#e5e7eb' : '#111827' }),
    singleValue: (base) => ({ ...base, color: isDark ? '#e5e7eb' : '#111827' }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: isDark
        ? 'rgba(59,130,246,0.25)'
        : 'rgba(59,130,246,0.12)',
      borderRadius: '4px',
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: isDark ? '#93c5fd' : '#1d4ed8',
      fontSize: '12px',
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: isDark ? '#93c5fd' : '#1d4ed8',
      '&:hover': {
        backgroundColor: isDark
          ? 'rgba(59,130,246,0.4)'
          : 'rgba(59,130,246,0.2)',
        color: isDark ? '#bfdbfe' : '#1e40af',
      },
    }),
    placeholder: (base) => ({ ...base, color: '#6b7280' }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#6b7280',
      '&:hover': { color: isDark ? '#9ca3af' : '#374151' },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#6b7280',
      '&:hover': { color: isDark ? '#9ca3af' : '#374151' },
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    }),
  };
}

const SAMPLE_OPTIONS: { value: SampleMode; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'p10', label: 'P10' },
  { value: 'p50', label: 'P50' },
  { value: 'p90', label: 'P90' },
  { value: 'p99', label: 'P99' },
  { value: 'p99.9', label: 'P99.9' },
  { value: 'p100', label: 'P100' },
];

const DAYS_OPTIONS: { value: DaysWindow; label: string }[] = [
  { value: 1, label: '1d' },
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 'all', label: 'All' },
];

const TIER_FILTER_OPTIONS: { value: TierFilter; label: string }[] = [
  { value: 'all', label: 'All tiers' },
  { value: 'max', label: 'Max' },
  { value: 'regular', label: 'Regular' },
  { value: 'quick', label: 'Quick' },
];

// ──────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────

interface FilterFieldProps {
  label: string;
  children: ReactNode;
}

function FilterField({ label, children }: FilterFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

function formatDollars(value: number, decimals = 4): string {
  return `$${value.toFixed(decimals)}`;
}

function formatThousands(value: number): string {
  return value.toLocaleString();
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
}

// ──────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────

export default function CostAnalyticsPage() {
  const navigate = useNavigate({ from: '/cost-analytics' });
  const routeSearch = useSearch({ from: '/cost-analytics' });
  const tier = routeSearch.tier;
  const selectedAgents = routeSearch.agents;
  const sample = routeSearch.sample;
  const days = routeSearch.days;

  // Changing tier clears the agent selection so chips from the previous tier
  // can't bleed into the new view.
  const setTier = (next: TierFilter) => {
    if (next === tier) return;
    navigate({
      search: { ...routeSearch, tier: next, agents: [] },
    });
  };

  const theme = useTheme();
  const chartOptions = useMemo(() => buildChartOptions(TOKENS[theme]), [theme]);
  const selectStyles = useMemo(() => buildSelectStyles(theme), [theme]);

  const agentsQuery = useCostAgents();
  const { tierBaselines, agentSeries } = useAgentTurnCurves(
    selectedAgents,
    sample,
    days,
    tier
  );

  const allResults = useMemo(
    () => [
      ...tierBaselines.map((t) => t.result),
      ...agentSeries.map((s) => s.result),
    ],
    [tierBaselines, agentSeries]
  );
  const isLoading = allResults.some((r) => r.isLoading);
  const isError = allResults.some((r) => r.isError);
  const isComparisonMode = selectedAgents.length > 0;

  const agentSelectOptions = (agentsQuery.data ?? [])
    .filter((info) => tier === 'all' || info.tier === tier)
    .map((info) => ({
      value: info.name,
      label: `${info.name} · ${TIER_LABELS[info.tier].toLowerCase()}`,
    }));

  // Fill missing integer turn positions with null y-values so Chart.js renders
  // gaps as breaks instead of interpolating a misleading diagonal line across
  // a stretch where no data actually exists. Backend trims the noisy tail; this
  // handles real per-agent gaps from per-turn agent routing.
  const densifyCurve = (
    points: ReadonlyArray<TurnCurvePoint>,
    yKey: 'avg_cost_usd' | 'avg_cumulative_cost_usd'
  ) => {
    if (points.length === 0) return [];
    const byPosition = new Map(points.map((p) => [p.turn_position, p]));
    const minPos = points[0].turn_position;
    const maxPos = points[points.length - 1].turn_position;
    const filled: Array<{
      x: number;
      y: number | null;
      avg_input_tokens?: number;
      avg_output_tokens?: number;
      avg_cached_read_tokens?: number;
      avg_cache_write_tokens?: number;
      avg_thought_tokens?: number;
    }> = [];
    for (let pos = minPos; pos <= maxPos; pos++) {
      const point = byPosition.get(pos);
      if (point) {
        filled.push({
          x: pos,
          y: point[yKey],
          avg_input_tokens: point.avg_input_tokens,
          avg_output_tokens: point.avg_output_tokens,
          avg_cached_read_tokens: point.avg_cached_read_tokens,
          avg_cache_write_tokens: point.avg_cache_write_tokens,
          avg_thought_tokens: point.avg_thought_tokens,
        });
      } else {
        filled.push({ x: pos, y: null });
      }
    }
    return filled;
  };

  const tierBaselineDatasets = (
    yKey: 'avg_cost_usd' | 'avg_cumulative_cost_usd'
  ) =>
    tierBaselines.map((baseline) => ({
      label: `${TIER_LABELS[baseline.tier]} (avg)`,
      data: densifyCurve(baseline.result.data?.turn_curve ?? [], yKey),
      borderColor: TIER_BASELINE_COLORS[baseline.tier],
      backgroundColor: 'transparent',
      borderDash: [5, 5],
      borderWidth: 1.5,
      pointRadius: 1,
      pointHoverRadius: 3,
      tension: 0,
      spanGaps: false,
      order: 1,
    }));

  const agentSeriesDatasets = (
    yKey: 'avg_cost_usd' | 'avg_cumulative_cost_usd'
  ) =>
    agentSeries.map((series, idx) => ({
      label: series.agent,
      data: densifyCurve(series.result.data?.turn_curve ?? [], yKey),
      borderColor: AGENT_COLORS[idx % AGENT_COLORS.length],
      backgroundColor: 'transparent',
      borderDash: [],
      borderWidth: 2.5,
      pointRadius: 3,
      pointHoverRadius: 5,
      tension: 0,
      spanGaps: false,
      order: 0,
    }));

  const perTurnDatasets = [
    ...tierBaselineDatasets('avg_cost_usd'),
    ...agentSeriesDatasets('avg_cost_usd'),
  ];
  const cumulativeDatasets = [
    ...tierBaselineDatasets('avg_cumulative_cost_usd'),
    ...agentSeriesDatasets('avg_cumulative_cost_usd'),
  ];

  const tierSummaries = tierBaselines.map((baseline) => ({
    tier: baseline.tier,
    summary: baseline.result.data?.summary as TurnCurveSummary | undefined,
  }));

  const hasData = allResults.some((r) => (r.data?.turn_curve.length ?? 0) > 0);

  // Pull min_samples_threshold from any loaded response so we can mention the
  // active floor in the chart subtitle.
  const minSamplesActive = allResults
    .map((r) => r.data?.summary?.min_samples_threshold)
    .find((v) => v !== undefined);

  // X-axis max — clamp to the deepest turn observed in the active focus. When
  // agents are selected we want the chart scoped to their reach (so the axis
  // isn't compressed by a denser tier baseline); otherwise use the deepest
  // tier baseline in scope. Falls back to Chart.js auto-scaling.
  const focusedResults = isComparisonMode
    ? agentSeries.map((s) => s.result)
    : tierBaselines.map((b) => b.result);
  const deepestTurn = focusedResults.reduce(
    (acc, r) => {
      const curve = r.data?.turn_curve ?? [];
      if (curve.length === 0) return acc;
      const last = curve[curve.length - 1].turn_position;
      return acc === undefined ? last : Math.max(acc, last);
    },
    undefined as number | undefined
  );
  // Pad by 5% so the rightmost point isn't flush against the chart edge.
  const xAxisMax =
    deepestTurn === undefined ? undefined : Math.ceil(deepestTurn * 1.05);

  const perTurnChartOptions = useMemo(
    () => ({
      ...chartOptions,
      scales: {
        ...chartOptions.scales,
        x: { ...chartOptions.scales.x, max: xAxisMax },
      },
    }),
    [chartOptions, xAxisMax]
  );

  return (
    <div className="space-y-6">
      {/* Page title */}
      <div>
        <h2 className="text-xl font-semibold">Per-Agent Cost Analytics</h2>
      </div>

      {/* Filters */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-start gap-x-5 gap-y-4">
          <FilterField label="Tier">
            <SegmentButton
              options={TIER_FILTER_OPTIONS}
              selected={tier}
              onSelect={(value) =>
                setTier(costAnalyticsSearchSchema.shape.tier.parse(value))
              }
            />
          </FilterField>

          <FilterField label="Length percentile">
            <SegmentButton
              options={SAMPLE_OPTIONS}
              selected={sample}
              onSelect={(value) =>
                navigate({
                  search: {
                    ...routeSearch,
                    sample: costAnalyticsSearchSchema.shape.sample.parse(value),
                  },
                })
              }
            />
          </FilterField>

          <FilterField label="Time range">
            <SegmentButton
              options={DAYS_OPTIONS}
              selected={days}
              onSelect={(value) =>
                navigate({
                  search: {
                    ...routeSearch,
                    days: costAnalyticsSearchSchema.shape.days.parse(value),
                  },
                })
              }
            />
          </FilterField>
        </div>

        <FilterField label="Agents">
          <Select
            isMulti
            options={agentSelectOptions}
            value={agentSelectOptions.filter((option) =>
              selectedAgents.includes(option.value)
            )}
            onChange={(selected) =>
              navigate({
                search: {
                  ...routeSearch,
                  agents: selected.map((option) => option.value),
                },
              })
            }
            placeholder={
              tier === 'all'
                ? 'All agents (combined)'
                : `${TIER_LABELS[tier]} agents`
            }
            isLoading={agentsQuery.isLoading}
            classNamePrefix="agent-select"
            styles={selectStyles}
          />
        </FilterField>
      </div>

      {/* Loading / error states */}
      {isLoading && (
        <div className="py-16 text-center text-muted-foreground text-sm">
          Loading…
        </div>
      )}
      {isError && (
        <div className="py-16 text-center text-destructive text-sm">
          Failed to load cost data. Please try again.
        </div>
      )}

      {/* Summary table — one row per tier (max / regular / quick) */}
      {!isLoading && !isError && (
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="grid grid-cols-[minmax(110px,1fr)_repeat(6,minmax(0,1fr))] gap-x-4 px-4 py-2 text-[10px] uppercase tracking-wide text-muted-foreground border-b">
            <span>Tier</span>
            <span className="text-right">Conversations</span>
            <span className="text-right">Total turns</span>
            <span className="text-right">Avg turns / conv</span>
            <span className="text-right">Avg cost / turn</span>
            <span className="text-right">Avg conv. cost</span>
            <span className="text-right">Total spend</span>
          </div>
          {tierSummaries.map(({ tier: rowTier, summary }, idx) => {
            const convs = summary?.total_conversations ?? 0;
            const turns = summary?.total_turns ?? 0;
            const avgConvCost = summary?.avg_total_conversation_cost_usd ?? 0;
            const avgTurnsPerConv = convs > 0 ? turns / convs : 0;
            const totalSpend = avgConvCost * convs;
            return (
              <div
                key={rowTier}
                className={`grid grid-cols-[minmax(110px,1fr)_repeat(6,minmax(0,1fr))] gap-x-4 px-4 py-2.5 items-center tabular-nums ${
                  idx < tierSummaries.length - 1 ? 'border-b' : ''
                }`}
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <span
                    className="inline-block w-2 h-2 rounded-full shrink-0"
                    style={{ background: TIER_BASELINE_COLORS[rowTier] }}
                  />
                  {TIER_LABELS[rowTier]}
                </span>
                <span className="text-right text-sm">
                  {formatThousands(convs)}
                </span>
                <span className="text-right text-sm">
                  {formatThousands(turns)}
                </span>
                <span className="text-right text-sm">
                  {avgTurnsPerConv.toFixed(1)}
                </span>
                <span className="text-right text-sm">
                  {formatDollars(summary?.avg_cost_per_turn_usd ?? 0, 5)}
                </span>
                <span className="text-right text-sm">
                  {formatDollars(avgConvCost, 4)}
                </span>
                <span className="text-right text-sm font-medium">
                  {formatDollars(totalSpend, 2)}
                </span>
              </div>
            );
          })}
          {tierSummaries.some(
            (t) => t.summary?.percentile_threshold != null
          ) && (
            <p className="px-4 py-2 text-[11px] text-muted-foreground border-t">
              {sample.toUpperCase()} length threshold (turn count, not cost; per
              tier):{' '}
              {tierSummaries
                .filter((t) => t.summary?.percentile_threshold != null)
                .map(
                  (t) =>
                    `${TIER_LABELS[t.tier]} ≥ ${t.summary?.percentile_threshold} turn${
                      t.summary?.percentile_threshold === 1 ? '' : 's'
                    }`
                )
                .join(' · ')}
            </p>
          )}
        </div>
      )}

      {/* Per-agent stats — shown beneath the tier table when agents are picked */}
      {!isLoading && !isError && isComparisonMode && (
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="grid grid-cols-[minmax(220px,2fr)_repeat(6,minmax(0,1fr))] gap-x-4 px-4 py-2 text-[10px] uppercase tracking-wide text-muted-foreground border-b">
            <span>Agent</span>
            <span className="text-right">Conversations</span>
            <span className="text-right">Total turns</span>
            <span className="text-right">Avg turns / conv</span>
            <span className="text-right">Avg cost / turn</span>
            <span className="text-right">Avg conv. cost</span>
            <span className="text-right">Total spend</span>
          </div>
          {agentSeries.map((series, idx) => {
            const summary = series.result.data?.summary;
            const convs = summary?.total_conversations ?? 0;
            const turns = summary?.total_turns ?? 0;
            const avgConvCost = summary?.avg_total_conversation_cost_usd ?? 0;
            const avgTurnsPerConv = convs > 0 ? turns / convs : 0;
            const totalSpend = avgConvCost * convs;
            const color = AGENT_COLORS[idx % AGENT_COLORS.length];
            return (
              <div
                key={series.agent}
                className={`grid grid-cols-[minmax(220px,2fr)_repeat(6,minmax(0,1fr))] gap-x-4 px-4 py-2.5 items-center tabular-nums ${
                  idx < agentSeries.length - 1 ? 'border-b' : ''
                }`}
              >
                <span className="flex items-center gap-2 text-sm font-medium truncate">
                  <span
                    className="inline-block w-2 h-2 rounded-full shrink-0"
                    style={{ background: color }}
                  />
                  <span className="truncate" title={series.agent}>
                    {series.agent}
                  </span>
                </span>
                <span className="text-right text-sm">
                  {formatThousands(convs)}
                </span>
                <span className="text-right text-sm">
                  {formatThousands(turns)}
                </span>
                <span className="text-right text-sm">
                  {avgTurnsPerConv.toFixed(1)}
                </span>
                <span className="text-right text-sm">
                  {formatDollars(summary?.avg_cost_per_turn_usd ?? 0, 5)}
                </span>
                <span className="text-right text-sm">
                  {formatDollars(avgConvCost, 4)}
                </span>
                <span className="text-right text-sm font-medium">
                  {formatDollars(totalSpend, 2)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Charts */}
      {!isLoading &&
        !isError &&
        (hasData ? (
          <div className="flex flex-col gap-6">
            {/* Chart 1 — Cost per turn */}
            <div className="rounded-lg border bg-card p-5 space-y-4">
              <div>
                <h3 className="text-sm font-semibold">Cost per turn</h3>
                <p className="text-xs text-muted-foreground">
                  Average LLM cost at each position in the conversation
                  {minSamplesActive !== undefined && (
                    <>
                      {' '}
                      — positions with fewer than{' '}
                      <strong>{minSamplesActive}</strong> contributing
                      conversation{minSamplesActive === 1 ? '' : 's'} are
                      hidden.
                    </>
                  )}
                </p>
              </div>
              <div style={{ height: 280, position: 'relative' }}>
                <Line
                  data={{ datasets: perTurnDatasets }}
                  options={perTurnChartOptions}
                />
              </div>
            </div>

            {/* Chart 2 — Cumulative cost */}
            <div className="rounded-lg border bg-card p-5 space-y-4">
              <div>
                <h3 className="text-sm font-semibold">
                  Cumulative conversation cost
                </h3>
                <p className="text-xs text-muted-foreground">
                  Average total spend by turn number. The tail may dip: at high
                  turns only the few conversations that lasted that long are
                  averaged in, so a single expensive chat dropping out pulls the
                  average down (survivorship bias, not a bug).
                </p>
              </div>
              <div style={{ height: 280, position: 'relative' }}>
                <Line
                  data={{ datasets: cumulativeDatasets }}
                  options={{
                    ...perTurnChartOptions,
                    scales: {
                      ...perTurnChartOptions.scales,
                      y: {
                        ...perTurnChartOptions.scales.y,
                        title: {
                          display: true,
                          text: 'Cumulative Cost (USD)',
                          font: { size: 11 },
                          color: TOKENS[theme].axisTitle,
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="py-16 text-center text-muted-foreground text-sm">
            No data for the selected filters.
          </div>
        ))}
    </div>
  );
}
